import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';

import { useTaskStore } from '@/stores/task-store';
import { useSeasonStore } from '@/stores/season-store';
import { usePropertyStore } from '@/stores/property-store';
import { Button } from '@/components/common/Button';
import { TaskCategory, TimingAnchor } from '@/types/task';
import { getCategoryDisplayName, getCategoryActions, getActionDisplayName } from '@/utils/category-utils';
import { DateField } from '@/components/forms/DateField';

const CATEGORY_OPTIONS = Object.values(TaskCategory);

function InputField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="field-label">
        {label}
        {required && <span className="text-[var(--color-accent)] ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}

// M-08 (Defect Batch M): inward outline so focus ring is never clipped (ADR-035).
const INPUT_CLASS =
  'w-full px-3 py-2.5 rounded-md bg-[var(--color-elevated)] border border-[var(--color-border)] text-body-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-placeholder)] focus:outline-2 focus:outline-[var(--color-accent)] focus:outline-offset-[-2px]';

export function TaskFormPage() {
  const { id: paramSeasonId, taskId } = useParams<{ id: string; taskId?: string }>();
  const [searchParams] = useSearchParams();
  // Support both /seasons/:id/tasks/new (old) and /prep/tasks/new?season=<id> (new)
  const seasonId = paramSeasonId ?? searchParams.get('season') ?? undefined;
  const navigate = useNavigate();
  const isEditMode = !!taskId && taskId !== 'new';

  const { tasks, loadTasks, addTask, updateTask } = useTaskStore();
  const { seasons, loadSeasons, getSeason } = useSeasonStore();
  const { properties, loadProperties } = usePropertyStore();

  interface FormValues {
    title: string;
    description: string;
    category: TaskCategory;
    selectedAction: string;
    dueDate: string;
    propertyId: string;
    notes: string;
    snoozedUntil: string;
  }

  const EMPTY_FORM: FormValues = {
    title: '',
    description: '',
    category: TaskCategory.Other,
    selectedAction: '',
    dueDate: '',
    propertyId: '',
    notes: '',
    snoozedUntil: '',
  };

  // For edit mode, derive initial values directly from tasks (no effect needed)
  const editTask = isEditMode && taskId ? tasks.find((t) => t.id === taskId) : undefined;
  const initialValues: FormValues = editTask
    ? {
        title: editTask.title,
        description: editTask.description ?? '',
        category: editTask.category ?? TaskCategory.Other,
        selectedAction: editTask.selectedAction ?? '',
        dueDate: editTask.dueDate,
        propertyId: editTask.propertyId ?? '',
        notes: editTask.notes ?? '',
        snoozedUntil: editTask.snoozedUntil ?? '',
      }
    : EMPTY_FORM;

  const [form, setForm] = useState<FormValues>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  // When edit task loads (tasks array populates after mount), sync form once
  useEffect(() => {
    if (!isEditMode || !taskId || form.title !== '') return;
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot sync from external prop on mount
    setForm({
      title: task.title,
      description: task.description ?? '',
      category: task.category ?? TaskCategory.Other,
      selectedAction: task.selectedAction ?? '',
      dueDate: task.dueDate,
      propertyId: task.propertyId ?? '',
      notes: task.notes ?? '',
      snoozedUntil: task.snoozedUntil ?? '',
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  useEffect(() => {
    if (seasons.length === 0) void loadSeasons();
    void loadProperties();
    if (seasonId && tasks.length === 0) void loadTasks(seasonId);
  }, [seasonId, seasons.length, tasks.length, loadSeasons, loadProperties, loadTasks]);

  function field<K extends keyof FormValues>(key: K) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };
  }

  const { title, description, category, selectedAction, dueDate, propertyId, notes, snoozedUntil } = form;

  const season = seasonId ? getSeason(seasonId) : undefined;
  const availableActions = getCategoryActions(category);

  function validate(): boolean {
    const newErrors: Record<string, string> = {};
    if (!title.trim()) newErrors.title = 'Title is required';
    if (title.trim().length > 200) newErrors.title = 'Title must be 200 characters or fewer';
    if (!dueDate) newErrors.dueDate = 'Due date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate() || !seasonId) return;

    setIsSaving(true);
    try {
      if (isEditMode && taskId) {
        await updateTask(taskId, {
          title: title.trim(),
          description: description.trim(),
          category,
          selectedAction: selectedAction || undefined,
          dueDate,
          propertyId: propertyId || undefined,
          notes: notes.trim() || undefined,
          snoozedUntil: snoozedUntil || undefined,
        });
        navigate(`/prep/tasks/${taskId}${seasonId ? `?season=${seasonId}` : ''}`);
      } else {
        await addTask({
          seasonId,
          title: title.trim(),
          description: description.trim(),
          category,
          selectedAction: selectedAction || undefined,
          dueDate,
          propertyId: propertyId || undefined,
          notes: notes.trim() || undefined,
          templateTaskId: null,
          blocked: false,
          completed: false,
          offsetDays: 0,
          timingAnchor: TimingAnchor.CalendarDate,
          sortOrder: tasks.length,
          dependsOn: [],
        });
        navigate(seasonId ? `/prep/tasks?season=${seasonId}` : '/prep/tasks');
      }
    } catch {
      setIsSaving(false);
    }
  }

  // Clear selectedAction when category changes
  function handleCategoryChange(newCategory: TaskCategory) {
    setForm((prev) => ({ ...prev, category: newCategory, selectedAction: '' }));
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to={
            isEditMode
              ? `/prep/tasks/${taskId}${seasonId ? `?season=${seasonId}` : ''}`
              : seasonId ? `/prep/tasks?season=${seasonId}` : '/prep/tasks'
          }
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
          aria-label={isEditMode ? 'Back to task detail' : 'Back to task list'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </Link>
        <h1 className="font-display text-display-sm text-[var(--color-text-display)]">
          {isEditMode ? 'Edit Task' : 'New Task'}
        </h1>
        {season && (
          <span className="text-body-xs text-[var(--color-text-muted)]">· {season.name}</span>
        )}
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5" data-testid="task-form">
        {/* Title */}
        <InputField label="Title" required>
          <input
            type="text"
            value={title}
            onChange={field('title')}
            maxLength={200}
            placeholder="e.g. Hang stands on North property"
            className={INPUT_CLASS}
            data-testid="input-title"
          />
          {errors.title && (
            <p className="text-body-xs text-[var(--color-accent)]" data-testid="error-title">{errors.title}</p>
          )}
        </InputField>

        {/* Description */}
        <InputField label="Description">
          <textarea
            value={description}
            onChange={field('description')}
            rows={3}
            placeholder="Optional details…"
            className={`${INPUT_CLASS} resize-y`}
            data-testid="input-description"
          />
        </InputField>

        {/* Category */}
        <InputField label="Category" required>
          <select
            value={category}
            onChange={(e) => handleCategoryChange(e.target.value as TaskCategory)}
            className={INPUT_CLASS}
            data-testid="select-category"
          >
            {CATEGORY_OPTIONS.map((cat) => (
              <option key={cat} value={cat}>
                {getCategoryDisplayName(cat)}
              </option>
            ))}
          </select>
        </InputField>

        {/* Predefined Action */}
        <InputField label="Action">
          {availableActions.length > 0 ? (
            <select
              value={selectedAction}
              onChange={field('selectedAction')}
              className={INPUT_CLASS}
              data-testid="select-action"
            >
              <option value="">— No action selected —</option>
              {availableActions.map((action) => (
                <option key={action} value={action}>
                  {getActionDisplayName(action)}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-body-xs text-[var(--color-text-muted)] py-1" data-testid="no-actions-message">
              No predefined actions for this category.
            </p>
          )}
        </InputField>

        {/* Due Date */}
        <InputField label="Due Date" required>
          <DateField
            id="task-due-date"
            value={dueDate}
            onChange={(v) => setForm((prev) => ({ ...prev, dueDate: v }))}
          />
          {errors.dueDate && (
            <p className="text-body-xs text-[var(--color-accent)]" data-testid="error-due-date">{errors.dueDate}</p>
          )}
        </InputField>

        {/* Property */}
        <InputField label="Property">
          <select
            value={propertyId}
            onChange={field('propertyId')}
            className={INPUT_CLASS}
            data-testid="select-property"
          >
            <option value="">— Not tied to a property —</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </InputField>

        {/* Notes */}
        <InputField label="Notes">
          <textarea
            value={notes}
            onChange={field('notes')}
            rows={3}
            placeholder="Optional notes…"
            className={`${INPUT_CLASS} resize-y`}
            data-testid="input-notes"
          />
        </InputField>

        {/* Snoozed Until — edit only */}
        {isEditMode && (
          <InputField label="Snoozed Until">
            <DateField
              id="task-snoozed-until"
              value={snoozedUntil}
              onChange={(v) => setForm((prev) => ({ ...prev, snoozedUntil: v }))}
            />
          </InputField>
        )}

        {/* Submit */}
        <Button
          type="submit"
          variant="primary"
          disabled={isSaving}
          className="w-full mt-2"
          data-testid="submit-button"
        >
          {isSaving ? 'Saving…' : isEditMode ? 'Save Changes' : 'Create Task'}
        </Button>
      </form>
    </div>
  );
}
